import { getValidToken, validateAndRefreshToken } from '../utils/tokenUtils';
import { apiConfig } from '../config';
import { store } from '../store';
import { logoutUser } from '../store/authSlice';
import { CompetitorAdvantage as CompAdvantage, CompetitorWeakness as CompWeakness } from '../types/competitor';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface ApiError {
  success: false;
  error: string;
  message: string;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface DictionaryVariation {
  _id: string;
  question: string;
  category?: string;
  needsEmbedding?: boolean;
  hasEmbedding?: boolean;
}

export interface DictionaryItem {
  _id: string;
  question: string;
  answer: string;
  category?: string;
  isOriginal?: boolean;
  parentId?: string | null;
  variations?: DictionaryVariation[];
  hasEmbedding?: boolean;
  needsEmbedding?: boolean;
  createdAt?: string;
  updatedAt?: string;
  version?: number;
}

export interface ObjectionsResponse {
  objections: DictionaryItem[];
  pagination: PaginationInfo | null;
}

export interface FAQsResponse {
  faqs: DictionaryItem[];
  pagination: PaginationInfo | null;
}

export interface SearchResponse {
  success: boolean;
  results: DictionaryItem[];
  pagination: PaginationInfo;
  error?: string;
}

class ApiService {
  private baseURL: string;
  
  constructor() {
    this.baseURL = apiConfig.baseURL;
    console.debug('[ApiService] Initialized with base URL:', this.baseURL);
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await getValidToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      console.debug('[ApiService] Added auth header with token (length:', token.length, ')');
    } else {
      console.debug('[ApiService] No auth token available');
    }
    
    return headers;
  }

  private async handleResponse<T>(response: Response, retryCount: number = 0): Promise<T> {
    const contentType = response.headers.get('content-type');
    let data: any;
    
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }
    
    console.debug('[ApiService] Response:', {
      status: response.status,
      statusText: response.statusText,
      url: response.url,
      data: data
    });
    
    // Handle 401 Unauthorized errors
    if (response.status === 401 && retryCount === 0) {
      console.warn('[ApiService] 401 Unauthorized, attempting token refresh...');
      
      // Try to refresh the token
      const { isValid } = await validateAndRefreshToken();
      
      if (isValid) {
        console.debug('[ApiService] Token refreshed successfully, retrying request...');
        // Token was refreshed, retry the request once
        // Note: The original request will be retried by the calling method
        const error: ApiError = {
          success: false,
          error: 'TOKEN_REFRESHED',
          message: 'Token was refreshed, retry the request'
        };
        throw error;
      } else {
        console.error('[ApiService] Token refresh failed, logging out user...');
        // Token refresh failed, log out the user
        store.dispatch(logoutUser());
        
        // Redirect to login page
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        
        const error: ApiError = {
          success: false,
          error: 'AUTHENTICATION_FAILED',
          message: 'Your session has expired. Please log in again.'
        };
        throw error;
      }
    }
    
    if (!response.ok) {
      const error: ApiError = {
        success: false,
        error: data.error || 'API_ERROR',
        message: data.message || `HTTP ${response.status}: ${response.statusText}`
      };
      
      console.error('[ApiService] API Error:', error);
      throw error;
    }
    
    return data;
  }

  async get<T = any>(endpoint: string, params?: Record<string, string>, retryCount: number = 0): Promise<ApiResponse<T>> {
    try {
      const url = new URL(`${this.baseURL}/api${endpoint}`);
      
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            url.searchParams.append(key, value);
          }
        });
      }
      
      console.debug('[ApiService] GET request:', url.toString());
      
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers,
      });
      
      return await this.handleResponse<ApiResponse<T>>(response, retryCount);
    } catch (error: any) {
      // If token was refreshed, retry the request once
      if (error.error === 'TOKEN_REFRESHED' && retryCount === 0) {
        console.debug('[ApiService] Retrying GET request after token refresh...');
        return this.get<T>(endpoint, params, 1);
      }
      console.error('[ApiService] GET request failed:', error);
      throw error;
    }
  }

  async post<T = any>(endpoint: string, data?: any, retryCount: number = 0): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/api${endpoint}`;

      console.debug('[ApiService] POST request:', url, 'with data:', data);

      const headers = await this.getAuthHeaders();

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: data !== undefined ? JSON.stringify(data) : undefined,
      });

      return await this.handleResponse<ApiResponse<T>>(response, retryCount);
    } catch (error: any) {
      // If token was refreshed, retry the request once
      if (error.error === 'TOKEN_REFRESHED' && retryCount === 0) {
        console.debug('[ApiService] Retrying POST request after token refresh...');
        return this.post<T>(endpoint, data, 1);
      }
      console.error('[ApiService] POST request failed:', error);
      throw error;
    }
  }

  async postFormData<T = any>(endpoint: string, formData: FormData, retryCount: number = 0): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/api${endpoint}`;

      console.debug('[ApiService] POST FormData request:', url);

      const token = await getValidToken();
      const headers: Record<string, string> = {};

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.debug('[ApiService] Added auth header for FormData upload');
      }

      // Don't set Content-Type for FormData - browser will set it with boundary

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });

      return await this.handleResponse<ApiResponse<T>>(response, retryCount);
    } catch (error: any) {
      // If token was refreshed, retry the request once
      if (error.error === 'TOKEN_REFRESHED' && retryCount === 0) {
        console.debug('[ApiService] Retrying FormData POST request after token refresh...');
        return this.postFormData<T>(endpoint, formData, 1);
      }
      console.error('[ApiService] POST FormData request failed:', error);
      throw error;
    }
  }

  async putFormData<T = any>(endpoint: string, formData: FormData, retryCount: number = 0): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/api${endpoint}`;

      console.debug('[ApiService] PUT FormData request:', url);

      const token = await getValidToken();
      const headers: Record<string, string> = {};

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.debug('[ApiService] Added auth header for FormData upload');
      }

      // Don't set Content-Type for FormData - browser will set it with boundary

      const response = await fetch(url, {
        method: 'PUT',
        headers,
        body: formData,
      });

      return await this.handleResponse<ApiResponse<T>>(response, retryCount);
    } catch (error: any) {
      // If token was refreshed, retry the request once
      if (error.error === 'TOKEN_REFRESHED' && retryCount === 0) {
        console.debug('[ApiService] Retrying FormData PUT request after token refresh...');
        return this.putFormData<T>(endpoint, formData, 1);
      }
      console.error('[ApiService] PUT FormData request failed:', error);
      throw error;
    }
  }

  async put<T = any>(endpoint: string, data?: any, retryCount: number = 0): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/api${endpoint}`;
      
      console.debug('[ApiService] PUT request:', url, 'with data:', data);
      
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(url, {
        method: 'PUT',
        headers,
        body: data !== undefined ? JSON.stringify(data) : undefined,
      });
      
      return await this.handleResponse<ApiResponse<T>>(response, retryCount);
    } catch (error: any) {
      // If token was refreshed, retry the request once
      if (error.error === 'TOKEN_REFRESHED' && retryCount === 0) {
        console.debug('[ApiService] Retrying PUT request after token refresh...');
        return this.put<T>(endpoint, data, 1);
      }
      console.error('[ApiService] PUT request failed:', error);
      throw error;
    }
  }

  async patch<T = any>(endpoint: string, data?: any, retryCount: number = 0): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/api${endpoint}`;

      console.debug('[ApiService] PATCH request:', url, 'with data:', data);

      const headers = await this.getAuthHeaders();

      const response = await fetch(url, {
        method: 'PATCH',
        headers,
        body: data !== undefined ? JSON.stringify(data) : undefined,
      });

      return await this.handleResponse<ApiResponse<T>>(response, retryCount);
    } catch (error: any) {
      // If token was refreshed, retry the request once
      if (error.error === 'TOKEN_REFRESHED' && retryCount === 0) {
        console.debug('[ApiService] Retrying PATCH request after token refresh...');
        return this.patch<T>(endpoint, data, 1);
      }
      console.error('[ApiService] PATCH request failed:', error);
      throw error;
    }
  }

  async delete<T = any>(endpoint: string, retryCount: number = 0): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseURL}/api${endpoint}`;
      
      console.debug('[ApiService] DELETE request:', url);
      
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers,
      });
      
      return await this.handleResponse<ApiResponse<T>>(response, retryCount);
    } catch (error: any) {
      // If token was refreshed, retry the request once
      if (error.error === 'TOKEN_REFRESHED' && retryCount === 0) {
        console.debug('[ApiService] Retrying DELETE request after token refresh...');
        return this.delete<T>(endpoint, 1);
      }
      console.error('[ApiService] DELETE request failed:', error);
      throw error;
    }
  }

  // Health check without auth
  async healthCheck(): Promise<ApiResponse> {
    try {
      const url = `${this.baseURL}/api/health`;
      
      console.debug('[ApiService] Health check:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      return await this.handleResponse<ApiResponse>(response);
    } catch (error) {
      console.error('[ApiService] Health check failed:', error);
      throw error;
    }
  }
}

// Create and export singleton instance
export const apiService = new ApiService();

// Export common API endpoints
export const authApi = {
  getCurrentUser: () => apiService.get('/auth/me'),
  updateProfile: (data: any) => apiService.put('/auth/profile', data),
  getUserStats: () => apiService.get('/auth/stats'),
  deleteAccount: () => apiService.delete('/auth/account'),
};

export const transcriptApi = {
  getTranscripts: (params?: Record<string, string>) => apiService.get('/transcripts', params),
  getTranscript: (id: string) => apiService.get(`/transcripts/${id}`),
  createTranscript: (data: any) => apiService.post('/transcripts', data),
  updateTranscript: (id: string, data: any) => apiService.put(`/transcripts/${id}`, data),
  deleteTranscript: (id: string) => apiService.delete(`/transcripts/${id}`),
  getTranscriptStats: () => apiService.get('/transcripts/stats'),
};

export const settingsApi = {
  getSettings: () => apiService.get('/settings'),
};

// Dictionary API endpoints with pagination
export const getDictionary = async (page: number = 1, limit: number = 20, category?: string) => {
  try {
    const params: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString()
    };
    if (category) {
      params.category = category;
    }
    const response = await apiService.get('/dictionary', params);
    return response?.data || null;
  } catch (error) {
    console.error('[Dictionary API] Error fetching dictionary:', error);
    return null;
  }
};

export const getObjectionsGrouped = async (page: number = 1, limit: number = 20, category?: string): Promise<ObjectionsResponse> => {
  try {
    const params: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString()
    };
    if (category) {
      params.category = category;
    }
    const response = await apiService.get<any>('/dictionary/objections/grouped', params);
    // The response is an ApiResponse, but the backend sends the data directly
    // Cast to any first to avoid type errors
    const data = response as any;
    return {
      objections: data?.objections || [],
      pagination: data?.pagination || null
    };
  } catch (error) {
    console.error('[Dictionary API] Error fetching grouped objections:', error);
    return { objections: [], pagination: null };
  }
};

export const getObjections = async (page: number = 1, limit: number = 20, category?: string): Promise<ObjectionsResponse> => {
  try {
    const params: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString()
    };
    if (category) {
      params.category = category;
    }
    const response = await apiService.get<any>('/dictionary/objections', params);
    // The response is an ApiResponse, but the backend sends the data directly
    // Cast to any first to avoid type errors
    const data = response as any;
    return {
      objections: data?.objections || [],
      pagination: data?.pagination || null
    };
  } catch (error) {
    console.error('[Dictionary API] Error fetching objections:', error);
    return { objections: [], pagination: null };
  }
};

export const getFAQs = async (page: number = 1, limit: number = 20, category?: string): Promise<FAQsResponse> => {
  try {
    const params: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString()
    };
    if (category) {
      params.category = category;
    }
    const response = await apiService.get<any>('/dictionary/faqs', params);
    // The response is an ApiResponse, but the backend sends the data directly
    // Cast to any first to avoid type errors
    const data = response as any;
    return {
      faqs: data?.faqs || [],
      pagination: data?.pagination || null
    };
  } catch (error) {
    console.error('[Dictionary API] Error fetching FAQs:', error);
    return { faqs: [], pagination: null };
  }
};

export const addObjection = async (question: string, answer: string, category?: string, isOriginal?: boolean, parentId?: string | null) => {
  try {
    const response = await apiService.post('/dictionary/objections', {
      question,
      answer,
      category: category || 'Original',
      isOriginal: isOriginal || false,
      parentId: parentId || null
    });
    console.log('the response is', response)
    return response || null;
  } catch (error) {
    console.error('[Dictionary API] Error adding objection:', error);
    throw error; // Re-throw to handle in component
  }
};

export const addObjectionVariation = async (parentId: string, question: string) => {
  try {
    const response = await apiService.post(`/dictionary/objections/${parentId}/variations`, {
      question
      // Don't send category for variations - only originals have topic categories
    });
    return response?.data || null;
  } catch (error) {
    console.error('[Dictionary API] Error adding variation:', error);
    throw error;
  }
};

export const updateObjection = async (id: string, data: { question?: string; answer?: string; category?: string }) => {
  try {
    const response = await apiService.put(`/dictionary/objections/${id}`, data);
    return response?.data || null;
  } catch (error) {
    console.error('[Dictionary API] Error updating objection:', error);
    throw error; // Re-throw to handle in component
  }
};

export const deleteObjection = async (id: string) => {
  try {
    const response = await apiService.delete(`/dictionary/objections/${id}`);
    return response?.data || null;
  } catch (error) {
    console.error('[Dictionary API] Error deleting objection:', error);
    throw error; // Re-throw to handle in component
  }
};

export const getFAQsGrouped = async (page: number = 1, limit: number = 20, category?: string): Promise<FAQsResponse> => {
  try {
    const params: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString()
    };
    if (category) {
      params.category = category;
    }
    const response = await apiService.get<any>('/dictionary/faqs/grouped', params);
    const data = response as any;
    return {
      faqs: data?.faqs || [],
      pagination: data?.pagination || null
    };
  } catch (error) {
    console.error('Error fetching grouped FAQs:', error);
    return { faqs: [], pagination: null };
  }
};

/**
 * Search objections and FAQs using Algolia via the backend.
 */
export const searchDictionary = async (
  query: string,
  type: 'objection' | 'faq' | 'all' = 'all',
  page: number = 1,
  limit: number = 20
): Promise<SearchResponse> => {
  try {
    const params: Record<string, string> = {
      q: query,
      type,
      page: page.toString(),
      limit: limit.toString(),
    };

    const response = await apiService.get<SearchResponse>('/dictionary/search', params);
    return response as unknown as SearchResponse;
  } catch (error: any) {
    console.error('[Search API] Search error:', error);
    throw new Error(error?.message || 'Search failed');
  }
};

export const addFAQVariation = async (parentId: string, question: string) => {
  try {
    const response = await apiService.post(`/dictionary/faqs/${parentId}/variations`, {
      question
      // Don't send category for variations - only originals have topic categories
    });
    return response?.data || null;
  } catch (error) {
    console.error('[Dictionary API] Error adding FAQ variation:', error);
    throw error;
  }
};

export const addFAQ = async (question: string, answer: string, category?: string) => {
  try {
    const response = await apiService.post('/dictionary/faqs', {
      question,
      answer,
      category: category || 'Original'
    });
    return response || null;
  } catch (error) {
    console.error('[Dictionary API] Error adding FAQ:', error);
    throw error; // Re-throw to handle in component
  }
};

export const updateFAQ = async (id: string, data: { question?: string; answer?: string; category?: string }) => {
  try {
    const response = await apiService.put(`/dictionary/faqs/${id}`, data);
    return response?.data || null;
  } catch (error) {
    console.error('[Dictionary API] Error updating FAQ:', error);
    throw error; // Re-throw to handle in component
  }
};

export const deleteFAQ = async (id: string) => {
  try {
    const response = await apiService.delete(`/dictionary/faqs/${id}`);
    return response?.data || null;
  } catch (error) {
    console.error('[Dictionary API] Error deleting FAQ:', error);
    throw error; // Re-throw to handle in component
  }
};

// Generate variations for an objection using AI (returns variations without saving)
export const generateObjectionVariations = async (objectionId: string, count: number = 3) => {
  try {
    const response = await apiService.post(`/dictionary/objections/${objectionId}/generate-variations`, {
      count
    });
    return response;
  } catch (error) {
    console.error('[Dictionary API] Error generating objection variations:', error);
    throw error;
  }
};

// Generate variations for a FAQ using AI (returns variations without saving)
export const generateFAQVariations = async (faqId: string, count: number = 3) => {
  try {
    const response = await apiService.post(`/dictionary/faqs/${faqId}/generate-variations`, {
      count
    });
    return response;
  } catch (error) {
    console.error('[Dictionary API] Error generating FAQ variations:', error);
    throw error;
  }
};

// Check for similar questions in the database
export const checkSimilarQuestions = async (question: string, type: 'objection' | 'faq' | 'both' = 'both', threshold: number = 0.5) => {
  try {
    const response = await apiService.post('/dictionary/check-similar', {
      question,
      type,
      threshold
    });
    return response;
  } catch (error) {
    console.error('[Dictionary API] Error checking similar questions:', error);
    throw error;
  }
};

// Landmine Management API
export interface LandmineItem {
  _id: string;
  trigger: string;
  triggerVariations: string[];
  landmineType: 'legal_risk' | 'detractor';
  category?: string;  // Required only for legal_risk type
  priority: 'high' | 'medium' | 'low';
  canSay?: Array<{
    point: string;
    context?: string;
  }>;
  cannotSay?: Array<{
    point: string;
    reason?: string;
    severity?: 'legal_risk' | 'compliance_risk' | 'brand_risk' | 'general';
  }>;
  isActive: boolean;
  quickTips?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

// Competitor interfaces - re-export from types
export type CompetitorAdvantage = CompAdvantage;
export type CompetitorWeakness = CompWeakness;

export interface CompetitorItem {
  _id: string;
  name: string;
  // Note: variations, regex_patterns, and metadata removed - detection is now LLM-based
  advantages: CompetitorAdvantage[];
  weaknesses: CompetitorWeakness[];
  talking_points: string;
  category: 'direct_competitor' | 'indirect_competitor' | 'alternative_solution';
  priority: 'high' | 'medium' | 'low';
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CompetitorsResponse {
  competitors: CompetitorItem[];
  pagination: PaginationInfo | null;
}

export interface LandminesResponse {
  landmines: LandmineItem[];
  pagination?: PaginationInfo;
}

// Get all landmines with pagination
export const getLandmines = async (
  page: number = 1,
  limit: number = 20,
  filters?: { category?: string; priority?: string; isActive?: boolean; landmineType?: string }
): Promise<{ landmines: LandmineItem[]; pagination: PaginationInfo | null }> => {
  try {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (filters?.category) params.append('category', filters.category);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString());
    if (filters?.landmineType) params.append('landmineType', filters.landmineType);

    let url = `/landmines?${params.toString()}`
    const response = await apiService.get<any>(url) as any;
    // Handle new response format with success field and pagination
    console.log('the response is', response)
    if (response?.success) {
      return {
        landmines: response.landmines || [],
        pagination: response.pagination || null
      };
    }
    // Fallback for old format
    return {
      landmines: [],
      pagination: null
    };
  } catch (error) {
    console.error('[Landmine API] Error getting landmines:', error);
    throw error;
  }
};

// Get landmines grouped by category
export const getLandminesGrouped = async (filters?: { category?: string; priority?: string; isActive?: boolean }): Promise<any> => {
  try {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString());
    
    const response = await apiService.get<any>(`/landmines/grouped?${params.toString()}`) as any;
    // Handle new response format with success field
    if (response?.success && response?.landmines) {
      return response.landmines;
    }
    // Fallback for direct response
    if (response && typeof response === 'object' && !response.success) {
      return response;
    }
    return {};
  } catch (error) {
    console.error('[Landmine API] Error getting grouped landmines:', error);
    throw error;
  }
};

// Get single landmine by ID
export const getLandmineById = async (id: string): Promise<LandmineItem | null> => {
  try {
    const response = await apiService.get<any>(`/landmines/${id}`) as any;
    // Handle new response format with success field - single item uses 'landmine' field
    if (response?.success && response?.landmine) {
      return response.landmine;
    }
    // Fallback for direct response
    if (response && typeof response === 'object' && !response.success) {
      return response as any;
    }
    return null;
  } catch (error) {
    console.error('[Landmine API] Error getting landmine:', error);
    throw error;
  }
};

// Add new landmine
export const addLandmine = async (landmineData: Partial<LandmineItem>): Promise<LandmineItem> => {
  try {
    const response = await apiService.post<any>('/landmines', landmineData) as any;
    // Handle new response format with success field - create uses 'landmine' field
    if (response?.success && response?.landmine) {
      return response.landmine;
    }
    // Fallback for direct response
    if (response && typeof response === 'object' && !response.success) {
      return response as any;
    }
    throw new Error('Invalid response format');
  } catch (error) {
    console.error('[Landmine API] Error adding landmine:', error);
    throw error;
  }
};

// Update landmine
export const updateLandmine = async (id: string, updates: Partial<LandmineItem>): Promise<LandmineItem> => {
  try {
    const response = await apiService.put<any>(`/landmines/${id}`, updates) as any;
    // Handle new response format with success field - update uses 'landmine' field
    if (response?.success && response?.landmine) {
      return response.landmine;
    }
    // Fallback for direct response
    if (response && typeof response === 'object' && !response.success) {
      return response as any;
    }
    throw new Error('Invalid response format');
  } catch (error) {
    console.error('[Landmine API] Error updating landmine:', error);
    throw error;
  }
};

// Delete landmine
export const deleteLandmine = async (id: string): Promise<void> => {
  try {
    const response = await apiService.delete<any>(`/landmines/${id}`) as any;
    // The delete endpoint returns success with a message and landmine field
    // We don't need to return anything, just ensure it succeeded
    if (!response?.success) {
      throw new Error(response?.error || 'Failed to delete landmine');
    }
  } catch (error) {
    console.error('[Landmine API] Error deleting landmine:', error);
    throw error;
  }
};

// Add trigger variation
export const addLandmineTriggerVariation = async (id: string, variation: string): Promise<LandmineItem> => {
  try {
    const response = await apiService.post<any>(`/landmines/${id}/variations`, { variation }) as any;
    // Handle new response format with success field - variations uses 'landmine' field
    if (response?.success && response?.landmine) {
      return response.landmine;
    }
    // Fallback for direct response
    if (response && typeof response === 'object' && !response.success) {
      return response as any;
    }
    throw new Error('Invalid response format');
  } catch (error) {
    console.error('[Landmine API] Error adding trigger variation:', error);
    throw error;
  }
};

// Remove trigger variation
export const removeLandmineTriggerVariation = async (id: string, variation: string): Promise<LandmineItem> => {
  try {
    // Since DELETE with body is not standard, we'll use a custom request
    const token = await getValidToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${apiConfig.baseURL}/api/landmines/${id}/variations`, {
      method: 'DELETE',
      headers,
      body: JSON.stringify({ variation }),
    });
    
    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.error || 'Failed to remove trigger variation');
    }
    
    // Handle new response format with success field - uses 'landmine' field (singular)
    if (result?.success && result?.landmine) {
      return result.landmine;
    }
    // Fallback for backward compatibility
    return result;
  } catch (error) {
    console.error('[Landmine API] Error removing trigger variation:', error);
    throw error;
  }
};

// Toggle landmine active status
export const toggleLandmineStatus = async (id: string): Promise<LandmineItem> => {
  try {
    // Use custom PATCH request
    const token = await getValidToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${apiConfig.baseURL}/api/landmines/${id}/toggle-status`, {
      method: 'PATCH',
      headers,
    });
    
    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.error || 'Failed to toggle landmine status');
    }
    
    // Handle new response format with success field
    if (result?.success && result?.landmine) {
      return result.landmine;
    }
    // Fallback for backward compatibility
    return result;
  } catch (error) {
    console.error('[Landmine API] Error toggling landmine status:', error);
    throw error;
  }
};

// Generate trigger variations using AI
export const generateLandmineVariations = async (trigger: string): Promise<string[]> => {
  try {
    const token = await getValidToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${apiConfig.baseURL}/api/landmines/generate-variations`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ trigger }),
    });

    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.error || 'Failed to generate variations');
    }

    if (result?.success && result?.variations) {
      return result.variations;
    }
    throw new Error('Failed to generate variations');
  } catch (error) {
    console.error('[Landmine API] Error generating variations:', error);
    throw error;
  }
};

// ============================================
// Competitor Management Functions
// ============================================

// Get all competitors
export const getCompetitors = async (page: number = 1, limit: number = 20): Promise<CompetitorsResponse> => {
  try {
    const response = await apiService.get<CompetitorsResponse>(`/competitors?page=${page}&limit=${limit}`);

    if (response?.success && response?.data) {
      return response.data;
    }

    // Fallback for different response structures
    if ((response as any)?.competitors) {
      return response as any;
    }

    return { competitors: [], pagination: null };
  } catch (error) {
    console.error('[Competitor API] Error fetching competitors:', error);
    throw error;
  }
};

// Get competitor by ID
export const getCompetitorById = async (id: string): Promise<CompetitorItem> => {
  try {
    const response = await apiService.get<CompetitorItem>(`/competitors/${id}`);

    if (response?.success && response?.data) {
      return response.data;
    }

    return response as any;
  } catch (error) {
    console.error('[Competitor API] Error fetching competitor:', error);
    throw error;
  }
};

// Create new competitor
export const addCompetitor = async (competitor: Partial<CompetitorItem>): Promise<CompetitorItem> => {
  try {
    const response = await apiService.post<CompetitorItem>('/competitors', competitor);

    if (response?.success && response?.data) {
      return response.data;
    }

    return response as any;
  } catch (error) {
    console.error('[Competitor API] Error creating competitor:', error);
    throw error;
  }
};

// Update competitor
export const updateCompetitor = async (id: string, updates: Partial<CompetitorItem>): Promise<CompetitorItem> => {
  try {
    const response = await apiService.put<CompetitorItem>(`/competitors/${id}`, updates);

    if (response?.success && response?.data) {
      return response.data;
    }

    return response as any;
  } catch (error) {
    console.error('[Competitor API] Error updating competitor:', error);
    throw error;
  }
};

// Delete competitor
export const deleteCompetitor = async (id: string): Promise<void> => {
  try {
    await apiService.delete(`/competitors/${id}`);
  } catch (error) {
    console.error('[Competitor API] Error deleting competitor:', error);
    throw error;
  }
};

// Add variation to competitor
// Add advantage to competitor
export const addCompetitorAdvantage = async (
  id: string,
  advantage: Partial<CompetitorAdvantage>
): Promise<CompetitorItem> => {
  try {
    const response = await apiService.post<CompetitorItem>(`/competitors/${id}/advantages`, advantage);

    if (response?.success && response?.data) {
      return response.data;
    }

    return response as any;
  } catch (error) {
    console.error('[Competitor API] Error adding advantage:', error);
    throw error;
  }
};

// Update advantage for competitor
export const updateCompetitorAdvantage = async (
  id: string,
  advantageId: string,
  advantage: Partial<CompetitorAdvantage>
): Promise<CompetitorItem> => {
  try {
    const response = await apiService.put<CompetitorItem>(`/competitors/${id}/advantages/${advantageId}`, advantage);

    if (response?.success && response?.data) {
      return response.data;
    }

    return response as any;
  } catch (error) {
    console.error('[Competitor API] Error updating advantage:', error);
    throw error;
  }
};

// Remove advantage from competitor
export const removeCompetitorAdvantage = async (id: string, advantageId: string): Promise<CompetitorItem> => {
  try {
    const response = await apiService.delete<CompetitorItem>(`/competitors/${id}/advantages/${advantageId}`);

    if (response?.success && response?.data) {
      return response.data;
    }

    return response as any;
  } catch (error) {
    console.error('[Competitor API] Error removing advantage:', error);
    throw error;
  }
};

// Add weakness to competitor
export const addCompetitorWeakness = async (
  id: string,
  weakness: Partial<CompetitorWeakness>
): Promise<CompetitorItem> => {
  try {
    const response = await apiService.post<CompetitorItem>(`/competitors/${id}/weaknesses`, weakness);

    if (response?.success && response?.data) {
      return response.data;
    }

    return response as any;
  } catch (error) {
    console.error('[Competitor API] Error adding weakness:', error);
    throw error;
  }
};

// Update weakness for competitor
export const updateCompetitorWeakness = async (
  id: string,
  weaknessId: string,
  weakness: Partial<CompetitorWeakness>
): Promise<CompetitorItem> => {
  try {
    const response = await apiService.put<CompetitorItem>(`/competitors/${id}/weaknesses/${weaknessId}`, weakness);

    if (response?.success && response?.data) {
      return response.data;
    }

    return response as any;
  } catch (error) {
    console.error('[Competitor API] Error updating weakness:', error);
    throw error;
  }
};

// Remove weakness from competitor
export const removeCompetitorWeakness = async (id: string, weaknessId: string): Promise<CompetitorItem> => {
  try {
    const response = await apiService.delete<CompetitorItem>(`/competitors/${id}/weaknesses/${weaknessId}`);

    if (response?.success && response?.data) {
      return response.data;
    }

    return response as any;
  } catch (error) {
    console.error('[Competitor API] Error removing weakness:', error);
    throw error;
  }
};

// Toggle competitor active status
export const toggleCompetitorStatus = async (id: string): Promise<CompetitorItem> => {
  try {
    const token = await getValidToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${apiConfig.baseURL}/api/competitors/${id}/toggle-status`, {
      method: 'PATCH',
      headers,
    });

    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.error || 'Failed to toggle competitor status');
    }

    return result;
  } catch (error) {
    console.error('[Competitor API] Error toggling competitor status:', error);
    throw error;
  }
};

// Generate regex patterns for competitor name using AI
// ============================================
// Calendar Events API
// ============================================

export interface CalendarEvent {
  id: string;
  title: string;
  startTime: string;
  endTime: string;
  attendees: Array<{
    email: string;
    displayName: string;
    responseStatus: string;
  }>;
  meetingUrl: string | null;
  isBlacklisted: boolean;
  hasBotScheduled: boolean;
  botStatus: 'scheduled' | 'creating' | 'joining' | 'active' | null;
}

export interface CalendarEventsResponse {
  success: boolean;
  events: CalendarEvent[];
  range: string;
  blacklistedCount: number;
}

// Get calendar events with optional time range filter
export const getCalendarEvents = async (range: '1week' | '2weeks' | '1month' = '1week'): Promise<CalendarEventsResponse> => {
  try {
    const response = await apiService.get<CalendarEventsResponse>(`/calendar/events?range=${range}`);
    return response as any;
  } catch (error) {
    console.error('[Calendar API] Error fetching calendar events:', error);
    throw error;
  }
};

// Blacklist a calendar event (prevent bot from auto-joining)
export const blacklistCalendarEvent = async (eventId: string): Promise<ApiResponse> => {
  try {
    const response = await apiService.post(`/calendar/events/${eventId}/blacklist`);
    return response;
  } catch (error) {
    console.error('[Calendar API] Error blacklisting event:', error);
    throw error;
  }
};

// Remove event from blacklist (allow bot to auto-join)
export const unblacklistCalendarEvent = async (eventId: string): Promise<ApiResponse> => {
  try {
    const response = await apiService.delete(`/calendar/events/${eventId}/blacklist`);
    return response;
  } catch (error) {
    console.error('[Calendar API] Error removing event from blacklist:', error);
    throw error;
  }
};

// ==========================================
// Bulk Upload API
// ==========================================

export interface BulkUploadJobError {
  row: number;
  sheet: string;
  message: string;
  originalData?: {
    question?: string;
    answer?: string;
  };
}

export interface BulkUploadJob {
  _id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'partial';
  fileName: string;
  uploadedBy?: string;
  totalObjections: number;
  totalFaqs: number;
  processedObjections: number;
  processedFaqs: number;
  failedObjections: number;
  failedFaqs: number;
  errors: BulkUploadJobError[];
  createdAt: string;
  updatedAt: string;
}

export interface BulkUploadJobsResponse {
  jobs: BulkUploadJob[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// Upload Excel file for bulk processing
export const uploadBulkFile = async (file: File): Promise<ApiResponse<{ job: BulkUploadJob }>> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiService.postFormData<{ job: BulkUploadJob }>('/bulk-upload', formData);
    return response;
  } catch (error) {
    console.error('[BulkUpload API] Error uploading file:', error);
    throw error;
  }
};

// Get all bulk upload jobs (history)
export const getBulkUploadJobs = async (page: number = 1, limit: number = 20): Promise<BulkUploadJobsResponse> => {
  try {
    const response = await apiService.get<any>(`/bulk-upload/jobs?page=${page}&limit=${limit}`) as any;
    if (response?.success) {
      return {
        jobs: response.jobs || [],
        pagination: response.pagination || { page: 1, limit: 20, total: 0, pages: 1 }
      };
    }
    return { jobs: [], pagination: { page: 1, limit: 20, total: 0, pages: 1 } };
  } catch (error) {
    console.error('[BulkUpload API] Error getting jobs:', error);
    throw error;
  }
};

// Get single job details
export const getBulkUploadJob = async (jobId: string): Promise<BulkUploadJob> => {
  try {
    const response = await apiService.get<any>(`/bulk-upload/jobs/${jobId}`) as any;
    if (response?.success && response?.job) {
      return response.job;
    }
    throw new Error('Job not found');
  } catch (error) {
    console.error('[BulkUpload API] Error getting job:', error);
    throw error;
  }
};

// Download template file
export const downloadBulkUploadTemplate = async (): Promise<void> => {
  try {
    const token = await getValidToken();
    const headers: Record<string, string> = {};

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${apiConfig.baseURL}/api/bulk-upload/template`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error('Failed to download template');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_upload_template.xlsx';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    console.error('[BulkUpload API] Error downloading template:', error);
    throw error;
  }
};

// Download errors CSV for a job
export const downloadJobErrors = async (jobId: string): Promise<void> => {
  try {
    const token = await getValidToken();
    const headers: Record<string, string> = {};

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${apiConfig.baseURL}/api/bulk-upload/jobs/${jobId}/errors`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to download errors');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `errors_${jobId}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    console.error('[BulkUpload API] Error downloading errors:', error);
    throw error;
  }
};

// ==========================================
// Knowledge Generator API
// ==========================================

export interface KnowledgeGeneratorVariation {
  tempId: string;
  question: string;
}

export interface KnowledgeGeneratorSuggestion {
  tempId: string;
  question: string;
  answer: string;
  category: string;
  variations: KnowledgeGeneratorVariation[];
  approved: boolean;
  edited: boolean;
  synced: boolean;
  syncedAt?: string;
}

export interface KnowledgeGeneratorProgress {
  currentStep: 'researching' | 'generating' | 'syncing' | null;
  percentage: number;
  message: string;
}

export interface KnowledgeGeneratorResearch {
  summary: string;
  products: string[];
  keyFeatures: string[];
  commonObjections: string[];
  pagesAnalyzed?: number;
  researchedAt: string;
}

export interface KnowledgeGeneratorError {
  message: string;
  step: string;
  timestamp: string;
  retryCount: number;
}

export interface KnowledgeGeneratorSyncResults {
  syncedAt: string;
  createdObjectionIds: string[];
  createdFaqIds: string[];
  totalObjections: number;
  totalFaqs: number;
  taskCompleted?: boolean;
  remainingCount?: number;
}

export interface KnowledgeGeneratorTask {
  _id: string;
  companyName: string;
  websiteUrl: string;
  companyDescription: string;
  crawledPagesCount?: number;
  createdBy: string;
  createdByEmail?: string;
  status: 'pending' | 'researching' | 'generating' | 'ready_for_review' | 'syncing' | 'completed' | 'error';
  progress: KnowledgeGeneratorProgress;
  researchContent?: KnowledgeGeneratorResearch;
  suggestions?: {
    objections: KnowledgeGeneratorSuggestion[];
    websiteFaqs: KnowledgeGeneratorSuggestion[];
    aiGeneratedFaqs: KnowledgeGeneratorSuggestion[];
  };
  syncResults?: KnowledgeGeneratorSyncResults;
  error?: KnowledgeGeneratorError;
  // Separate sync status (doesn't affect main task status)
  syncStatus?: 'idle' | 'syncing' | 'success' | 'error' | null;
  syncError?: KnowledgeGeneratorError;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  // Counts returned from list API (aggregation)
  suggestedObjectionsCount?: number;
  suggestedWebsiteFaqsCount?: number;
  suggestedAiGeneratedFaqsCount?: number;
  suggestedFaqsCount?: number; // Combined count for backwards compatibility
}

export interface KnowledgeGeneratorTasksResponse {
  data: KnowledgeGeneratorTask[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface DuplicateMatch {
  _id: string;
  question: string;
  answer: string;
  category: string;
  similarity: number;
  confidenceLevel: 'high' | 'medium' | 'low';
}

export interface DuplicateCheckResult {
  suggestion: KnowledgeGeneratorSuggestion;
  matches: DuplicateMatch[];
  type: 'objection' | 'websiteFaq' | 'aiGeneratedFaq';
}

export interface DuplicateCheckResponse {
  duplicates: {
    objections: DuplicateCheckResult[];
    websiteFaqs: DuplicateCheckResult[];
    aiGeneratedFaqs: DuplicateCheckResult[];
  };
  grouped: {
    high: DuplicateCheckResult[];
    medium: DuplicateCheckResult[];
    low: DuplicateCheckResult[];
  };
  noDuplicates: {
    objections: KnowledgeGeneratorSuggestion[];
    websiteFaqs: KnowledgeGeneratorSuggestion[];
    aiGeneratedFaqs: KnowledgeGeneratorSuggestion[];
  };
  summary: {
    totalApproved: number;
    withDuplicates: number;
    noDuplicates: number;
    highConfidence: number;
    mediumConfidence: number;
    lowConfidence: number;
  };
}

// Create a new knowledge generator task
export const createKnowledgeGeneratorTask = async (
  companyName: string,
  websiteUrl: string,
  companyDescription: string
): Promise<KnowledgeGeneratorTask> => {
  try {
    const response = await apiService.post<any>('/knowledge-generator/tasks', {
      companyName,
      websiteUrl,
      companyDescription
    });
    if (response?.success && response?.data) {
      return response.data;
    }
    throw new Error(response?.error || 'Failed to create task');
  } catch (error) {
    console.error('[KnowledgeGenerator API] Error creating task:', error);
    throw error;
  }
};

// Get all knowledge generator tasks
export const getKnowledgeGeneratorTasks = async (
  page: number = 1,
  limit: number = 20,
  status?: string
): Promise<KnowledgeGeneratorTasksResponse> => {
  try {
    const params: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString()
    };
    if (status) {
      params.status = status;
    }
    const response = await apiService.get<any>('/knowledge-generator/tasks', params) as any;
    if (response?.success) {
      return {
        data: response.data || [],
        pagination: response.pagination || { page: 1, limit: 20, total: 0, pages: 1 }
      };
    }
    return { data: [], pagination: { page: 1, limit: 20, total: 0, pages: 1 } };
  } catch (error) {
    console.error('[KnowledgeGenerator API] Error getting tasks:', error);
    throw error;
  }
};

// Get single knowledge generator task
export const getKnowledgeGeneratorTask = async (taskId: string): Promise<KnowledgeGeneratorTask> => {
  try {
    const response = await apiService.get<any>(`/knowledge-generator/tasks/${taskId}`) as any;
    if (response?.success && response?.data) {
      return response.data;
    }
    throw new Error('Task not found');
  } catch (error) {
    console.error('[KnowledgeGenerator API] Error getting task:', error);
    throw error;
  }
};

// Update suggestions (bulk update)
export const updateKnowledgeGeneratorSuggestions = async (
  taskId: string,
  suggestions: {
    objections?: KnowledgeGeneratorSuggestion[];
    websiteFaqs?: KnowledgeGeneratorSuggestion[];
    aiGeneratedFaqs?: KnowledgeGeneratorSuggestion[];
  }
): Promise<KnowledgeGeneratorTask> => {
  try {
    const response = await apiService.put<any>(`/knowledge-generator/tasks/${taskId}/suggestions`, {
      suggestions
    }) as any;
    if (response?.success && response?.data) {
      return response.data;
    }
    throw new Error(response?.error || 'Failed to update suggestions');
  } catch (error) {
    console.error('[KnowledgeGenerator API] Error updating suggestions:', error);
    throw error;
  }
};

// Update a single suggestion
export const updateKnowledgeGeneratorSuggestion = async (
  taskId: string,
  tempId: string,
  type: 'objection' | 'websiteFaq' | 'aiGeneratedFaq',
  updates: Partial<KnowledgeGeneratorSuggestion>
): Promise<void> => {
  try {
    const response = await apiService.put<any>(`/knowledge-generator/tasks/${taskId}/suggestions/${tempId}`, {
      type,
      updates
    }) as any;
    if (!response?.success) {
      throw new Error(response?.error || 'Failed to update suggestion');
    }
  } catch (error) {
    console.error('[KnowledgeGenerator API] Error updating suggestion:', error);
    throw error;
  }
};

// Check for duplicates before syncing
export const checkKnowledgeGeneratorDuplicates = async (
  taskId: string,
  threshold: number = 0.5
): Promise<DuplicateCheckResponse> => {
  try {
    const response = await apiService.post<any>(`/knowledge-generator/tasks/${taskId}/check-duplicates`, {
      threshold
    }) as any;
    if (response?.success && response?.data) {
      return response.data;
    }
    throw new Error(response?.error || 'Failed to check duplicates');
  } catch (error) {
    console.error('[KnowledgeGenerator API] Error checking duplicates:', error);
    throw error;
  }
};

// Sync approved items to knowledge base
export const syncKnowledgeGeneratorTask = async (
  taskId: string,
  objectionTempIds?: string[],
  faqTempIds?: string[],
  addAsVariations?: Array<{ tempId: string; parentId: string; type: 'objection' | 'faq' }>
): Promise<KnowledgeGeneratorSyncResults> => {
  try {
    const response = await apiService.post<any>(`/knowledge-generator/tasks/${taskId}/sync`, {
      objectionTempIds,
      faqTempIds,
      addAsVariations
    }) as any;
    if (response?.success && response?.data) {
      return response.data;
    }
    throw new Error(response?.error || 'Failed to sync task');
  } catch (error) {
    console.error('[KnowledgeGenerator API] Error syncing task:', error);
    throw error;
  }
};

// Retry failed task
export const retryKnowledgeGeneratorTask = async (taskId: string): Promise<KnowledgeGeneratorTask> => {
  try {
    const response = await apiService.post<any>(`/knowledge-generator/tasks/${taskId}/retry`) as any;
    if (response?.success && response?.data) {
      return response.data;
    }
    throw new Error(response?.error || 'Failed to retry task');
  } catch (error) {
    console.error('[KnowledgeGenerator API] Error retrying task:', error);
    throw error;
  }
};

// Delete task
export const deleteKnowledgeGeneratorTask = async (taskId: string): Promise<void> => {
  try {
    const response = await apiService.delete<any>(`/knowledge-generator/tasks/${taskId}`) as any;
    if (!response?.success) {
      throw new Error(response?.error || 'Failed to delete task');
    }
  } catch (error) {
    console.error('[KnowledgeGenerator API] Error deleting task:', error);
    throw error;
  }
};

// Get available categories
export const getKnowledgeGeneratorCategories = async (): Promise<string[]> => {
  try {
    const response = await apiService.get<any>('/knowledge-generator/categories') as any;
    if (response?.success && response?.data) {
      return response.data;
    }
    return [];
  } catch (error) {
    console.error('[KnowledgeGenerator API] Error getting categories:', error);
    throw error;
  }
};

// ============================================
// Sales Playbook Management Functions
// ============================================

export type SalesPlaybookTag = 'situation' | 'pain' | 'cost_math' | 'authority' | 'timeline' | 'close_trigger';

export interface SalesPlaybookPitchBullet {
  _id: string;
  text: string;
  sortOrder: number;
}

export interface SalesPlaybookQuestion {
  _id: string;
  questionText: string;
  tags: SalesPlaybookTag[];
  sortOrder: number;
  isActive: boolean;
}

export interface SalesPlaybookRole {
  _id: string;
  name: string;
  displayName: string;
  sortOrder: number;
  isActive: boolean;
  pitch: SalesPlaybookPitchBullet[];
  advanced: SalesPlaybookPitchBullet[];
  questions: SalesPlaybookQuestion[];
}

export interface SalesPlaybookIndustry {
  _id: string;
  industry: string;
  displayName: string;
  roles: SalesPlaybookRole[];
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface SalesPlaybooksResponse {
  salesPlaybooks: SalesPlaybookIndustry[];
  pagination: PaginationInfo | null;
}

export interface SalesPlaybookTagsResponse {
  tags: SalesPlaybookTag[];
  labels: Record<SalesPlaybookTag, string>;
}

// Playbook Library types
export type PlaybookLibraryType = 'pitch' | 'question' | 'advanced';

export interface PlaybookLibraryItem {
  _id: string;
  type: PlaybookLibraryType;
  text: string;
  sortOrder: number;
  isActive: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PlaybookLibraryResponse {
  items: PlaybookLibraryItem[];
  pagination: PaginationInfo | null;
}

// Backward compatibility type aliases
export type TalkTrackTag = SalesPlaybookTag;
export type TalkTrackPitchBullet = SalesPlaybookPitchBullet;
export type TalkTrackQuestion = SalesPlaybookQuestion;
export type TalkTrackRole = SalesPlaybookRole;
export type TalkTrackIndustry = SalesPlaybookIndustry;

// Get available tags
export const getSalesPlaybookTags = async (): Promise<SalesPlaybookTagsResponse> => {
  try {
    const response = await apiService.get<SalesPlaybookTagsResponse>('/salesplaybooks/tags');
    if (response?.success && response?.data) {
      return response.data;
    }
    return response as any;
  } catch (error) {
    console.error('[SalesPlaybook API] Error fetching tags:', error);
    throw error;
  }
};

// Get active industries for dropdown
export const getSalesPlaybookIndustries = async (): Promise<Array<{ industry: string; displayName: string; roles: Array<{ name: string; displayName: string }> }>> => {
  try {
    const response = await apiService.get<any>('/salesplaybooks/industries');
    if (response?.success && response?.data) {
      return response.data;
    }
    if (Array.isArray(response)) {
      return response;
    }
    return [];
  } catch (error) {
    console.error('[SalesPlaybook API] Error fetching industries:', error);
    throw error;
  }
};

// Get all sales playbooks (admin)
export const getSalesPlaybooks = async (page: number = 1, limit: number = 50): Promise<SalesPlaybooksResponse> => {
  try {
    const response = await apiService.get<SalesPlaybooksResponse>(`/salesplaybooks?page=${page}&limit=${limit}`);
    if (response?.success && response?.data) {
      return response.data;
    }
    if ((response as any)?.salesPlaybooks) {
      return response as any;
    }
    return { salesPlaybooks: [], pagination: null };
  } catch (error) {
    console.error('[SalesPlaybook API] Error fetching sales playbook:', error);
    throw error;
  }
};

// Get sales playbook by industry
export const getSalesPlaybookByIndustry = async (industry: string): Promise<SalesPlaybookIndustry | null> => {
  try {
    const response = await apiService.get<SalesPlaybookIndustry>(`/salesplaybooks/${industry}`);
    if (response?.success && response?.data) {
      return response.data;
    }
    return response as any;
  } catch (error) {
    console.error('[SalesPlaybook API] Error fetching sales playbook:', error);
    throw error;
  }
};

// Create new industry
export const createSalesPlaybook = async (displayName: string): Promise<SalesPlaybookIndustry> => {
  try {
    const response = await apiService.post<SalesPlaybookIndustry>('/salesplaybooks', { displayName });
    if (response?.success && response?.data) {
      return response.data;
    }
    return response as any;
  } catch (error) {
    console.error('[SalesPlaybook API] Error creating sales playbook:', error);
    throw error;
  }
};

// Update industry
export const updateSalesPlaybook = async (industry: string, updates: Partial<SalesPlaybookIndustry>): Promise<SalesPlaybookIndustry> => {
  try {
    const response = await apiService.put<SalesPlaybookIndustry>(`/salesplaybooks/${industry}`, updates);
    if (response?.success && response?.data) {
      return response.data;
    }
    return response as any;
  } catch (error) {
    console.error('[SalesPlaybook API] Error updating sales playbook:', error);
    throw error;
  }
};

// Delete industry
export const deleteSalesPlaybook = async (industry: string): Promise<void> => {
  try {
    await apiService.delete(`/salesplaybooks/${industry}`);
  } catch (error) {
    console.error('[SalesPlaybook API] Error deleting sales playbook:', error);
    throw error;
  }
};

// Toggle sales playbook status
export const toggleSalesPlaybookStatus = async (industry: string): Promise<SalesPlaybookIndustry> => {
  try {
    const response = await apiService.patch<SalesPlaybookIndustry>(`/salesplaybooks/${industry}/toggle-status`);
    if (response?.success && response?.data) {
      return response.data;
    }
    return response as any;
  } catch (error) {
    console.error('[SalesPlaybook API] Error toggling sales playbook status:', error);
    throw error;
  }
};

// Add role to industry
export const addSalesPlaybookRole = async (industry: string, displayName: string): Promise<SalesPlaybookIndustry> => {
  try {
    const response = await apiService.post<SalesPlaybookIndustry>(`/salesplaybooks/${industry}/roles`, { displayName });
    if (response?.success && response?.data) {
      return response.data;
    }
    return response as any;
  } catch (error) {
    console.error('[SalesPlaybook API] Error adding role:', error);
    throw error;
  }
};

// Update role
export const updateSalesPlaybookRole = async (industry: string, roleId: string, updates: Partial<SalesPlaybookRole>): Promise<SalesPlaybookIndustry> => {
  try {
    const response = await apiService.put<SalesPlaybookIndustry>(`/salesplaybooks/${industry}/roles/${roleId}`, updates);
    if (response?.success && response?.data) {
      return response.data;
    }
    return response as any;
  } catch (error) {
    console.error('[SalesPlaybook API] Error updating role:', error);
    throw error;
  }
};

// Delete role
export const deleteSalesPlaybookRole = async (industry: string, roleId: string): Promise<SalesPlaybookIndustry> => {
  try {
    const response = await apiService.delete<SalesPlaybookIndustry>(`/salesplaybooks/${industry}/roles/${roleId}`);
    if (response?.success && response?.data) {
      return response.data;
    }
    return response as any;
  } catch (error) {
    console.error('[SalesPlaybook API] Error deleting role:', error);
    throw error;
  }
};

// Add pitch bullet to role
export const addSalesPlaybookPitchBullet = async (industry: string, roleId: string, text: string): Promise<SalesPlaybookIndustry> => {
  try {
    const response = await apiService.post<SalesPlaybookIndustry>(`/salesplaybooks/${industry}/roles/${roleId}/pitch`, { text });
    if (response?.success && response?.data) {
      return response.data;
    }
    return response as any;
  } catch (error) {
    console.error('[SalesPlaybook API] Error adding pitch bullet:', error);
    throw error;
  }
};

// Update pitch bullet
export const updateSalesPlaybookPitchBullet = async (industry: string, roleId: string, pitchId: string, text: string): Promise<SalesPlaybookIndustry> => {
  try {
    const response = await apiService.put<SalesPlaybookIndustry>(`/salesplaybooks/${industry}/roles/${roleId}/pitch/${pitchId}`, { text });
    if (response?.success && response?.data) {
      return response.data;
    }
    return response as any;
  } catch (error) {
    console.error('[SalesPlaybook API] Error updating pitch bullet:', error);
    throw error;
  }
};

// Delete pitch bullet
export const deleteSalesPlaybookPitchBullet = async (industry: string, roleId: string, pitchId: string): Promise<SalesPlaybookIndustry> => {
  try {
    const response = await apiService.delete<SalesPlaybookIndustry>(`/salesplaybooks/${industry}/roles/${roleId}/pitch/${pitchId}`);
    if (response?.success && response?.data) {
      return response.data;
    }
    return response as any;
  } catch (error) {
    console.error('[SalesPlaybook API] Error deleting pitch bullet:', error);
    throw error;
  }
};

// Add advanced bullet to role
export const addSalesPlaybookAdvancedBullet = async (industry: string, roleId: string, text: string): Promise<SalesPlaybookIndustry> => {
  try {
    const response = await apiService.post<SalesPlaybookIndustry>(`/salesplaybooks/${industry}/roles/${roleId}/advanced`, { text });
    if (response?.success && response?.data) {
      return response.data;
    }
    return response as any;
  } catch (error) {
    console.error('[SalesPlaybook API] Error adding advanced bullet:', error);
    throw error;
  }
};

// Update advanced bullet
export const updateSalesPlaybookAdvancedBullet = async (industry: string, roleId: string, advancedId: string, text: string): Promise<SalesPlaybookIndustry> => {
  try {
    const response = await apiService.put<SalesPlaybookIndustry>(`/salesplaybooks/${industry}/roles/${roleId}/advanced/${advancedId}`, { text });
    if (response?.success && response?.data) {
      return response.data;
    }
    return response as any;
  } catch (error) {
    console.error('[SalesPlaybook API] Error updating advanced bullet:', error);
    throw error;
  }
};

// Delete advanced bullet
export const deleteSalesPlaybookAdvancedBullet = async (industry: string, roleId: string, advancedId: string): Promise<SalesPlaybookIndustry> => {
  try {
    const response = await apiService.delete<SalesPlaybookIndustry>(`/salesplaybooks/${industry}/roles/${roleId}/advanced/${advancedId}`);
    if (response?.success && response?.data) {
      return response.data;
    }
    return response as any;
  } catch (error) {
    console.error('[SalesPlaybook API] Error deleting advanced bullet:', error);
    throw error;
  }
};

// Add question to role
export const addSalesPlaybookQuestion = async (industry: string, roleId: string, questionText: string, tags: SalesPlaybookTag[]): Promise<SalesPlaybookIndustry> => {
  try {
    const response = await apiService.post<SalesPlaybookIndustry>(`/salesplaybooks/${industry}/roles/${roleId}/questions`, { questionText, tags });
    if (response?.success && response?.data) {
      return response.data;
    }
    return response as any;
  } catch (error) {
    console.error('[SalesPlaybook API] Error adding question:', error);
    throw error;
  }
};

// Update question
export const updateSalesPlaybookQuestion = async (industry: string, roleId: string, questionId: string, updates: Partial<SalesPlaybookQuestion>): Promise<SalesPlaybookIndustry> => {
  try {
    const response = await apiService.put<SalesPlaybookIndustry>(`/salesplaybooks/${industry}/roles/${roleId}/questions/${questionId}`, updates);
    if (response?.success && response?.data) {
      return response.data;
    }
    return response as any;
  } catch (error) {
    console.error('[SalesPlaybook API] Error updating question:', error);
    throw error;
  }
};

// Delete question
export const deleteSalesPlaybookQuestion = async (industry: string, roleId: string, questionId: string): Promise<SalesPlaybookIndustry> => {
  try {
    const response = await apiService.delete<SalesPlaybookIndustry>(`/salesplaybooks/${industry}/roles/${roleId}/questions/${questionId}`);
    if (response?.success && response?.data) {
      return response.data;
    }
    return response as any;
  } catch (error) {
    console.error('[SalesPlaybook API] Error deleting question:', error);
    throw error;
  }
};

// Bulk update all roles for an industry (single API call)
export const bulkUpdateSalesPlaybookRoles = async (
  industry: string,
  roles: {
    originalId?: string;
    displayName: string;
    pitchBullets: string[];
    advancedBullets: string[];
    questions: { text: string; tags: SalesPlaybookTag[] }[];
  }[]
): Promise<SalesPlaybookIndustry> => {
  try {
    const response = await apiService.put<SalesPlaybookIndustry>(
      `/salesplaybooks/${industry}/bulk-update-roles`,
      { roles }
    );
    if (response?.success && response?.data) {
      return response.data;
    }
    return response as any;
  } catch (error) {
    console.error('[SalesPlaybook API] Error bulk updating roles:', error);
    throw error;
  }
};

// ========== Playbook Dictionary API ==========

export const getPlaybookLibraryItems = async (
  type?: PlaybookLibraryType,
  page: number = 1,
  limit: number = 500,
  search?: string
): Promise<PlaybookLibraryResponse> => {
  try {
    let endpoint = `/salesplaybook-library?page=${page}&limit=${limit}`;
    if (type) endpoint += `&type=${type}`;
    if (search) endpoint += `&search=${encodeURIComponent(search)}`;

    const response = await apiService.get<PlaybookLibraryResponse>(endpoint);
    if (response?.success && (response as any)?.items) {
      return { items: (response as any).items, pagination: (response as any).pagination };
    }
    if (response?.success && response?.data) {
      return response.data;
    }
    return { items: [], pagination: null };
  } catch (error) {
    console.error('[PlaybookLibrary API] Error fetching items:', error);
    throw error;
  }
};

export const createPlaybookLibraryItem = async (
  type: PlaybookLibraryType,
  text: string
): Promise<PlaybookLibraryItem> => {
  try {
    const response = await apiService.post<{ item: PlaybookLibraryItem }>(
      '/salesplaybook-library',
      { type, text }
    );
    if (response?.success && (response as any)?.item) {
      return (response as any).item;
    }
    if (response?.success && response?.data) {
      return (response.data as any).item || response.data;
    }
    return response as any;
  } catch (error) {
    console.error('[PlaybookLibrary API] Error creating item:', error);
    throw error;
  }
};

export const updatePlaybookLibraryItem = async (
  id: string,
  text: string
): Promise<PlaybookLibraryItem> => {
  try {
    const response = await apiService.put<{ item: PlaybookLibraryItem }>(
      `/salesplaybook-library/${id}`,
      { text }
    );
    if (response?.success && (response as any)?.item) {
      return (response as any).item;
    }
    if (response?.success && response?.data) {
      return (response.data as any).item || response.data;
    }
    return response as any;
  } catch (error) {
    console.error('[PlaybookLibrary API] Error updating item:', error);
    throw error;
  }
};

export const deletePlaybookLibraryItem = async (id: string): Promise<void> => {
  try {
    await apiService.delete(`/salesplaybook-library/${id}`);
  } catch (error) {
    console.error('[PlaybookLibrary API] Error deleting item:', error);
    throw error;
  }
};

export const uploadPlaybookLibraryExcel = async (
  file: File
): Promise<{ message: string; counts: { pitch: number; question: number; advanced: number } }> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiService.postFormData<any>('/salesplaybook-library/bulk-upload', formData);
    if (response?.success) {
      return { message: (response as any).message, counts: (response as any).counts };
    }
    return response as any;
  } catch (error) {
    console.error('[PlaybookLibrary API] Error uploading Excel:', error);
    throw error;
  }
};

export const downloadPlaybookLibraryTemplate = async (): Promise<void> => {
  try {
    const token = await getValidToken();
    const url = `${apiConfig.baseURL}/api/salesplaybook-library/template`;
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      throw new Error('Failed to download template');
    }

    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = 'playbook_library_template.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('[PlaybookLibrary API] Error downloading template:', error);
    throw error;
  }
};

// Backward compatibility aliases
export const getTalkTrackTags = getSalesPlaybookTags;
export const getTalkTrackIndustries = getSalesPlaybookIndustries;
export const getTalkTracks = getSalesPlaybooks;
export const getTalkTrackByIndustry = getSalesPlaybookByIndustry;
export const createTalkTrack = createSalesPlaybook;
export const updateTalkTrack = updateSalesPlaybook;
export const deleteTalkTrack = deleteSalesPlaybook;
export const toggleTalkTrackStatus = toggleSalesPlaybookStatus;
export const addTalkTrackRole = addSalesPlaybookRole;
export const updateTalkTrackRole = updateSalesPlaybookRole;
export const deleteTalkTrackRole = deleteSalesPlaybookRole;
export const addTalkTrackPitchBullet = addSalesPlaybookPitchBullet;
export const updateTalkTrackPitchBullet = updateSalesPlaybookPitchBullet;
export const deleteTalkTrackPitchBullet = deleteSalesPlaybookPitchBullet;
export const addTalkTrackQuestion = addSalesPlaybookQuestion;
export const updateTalkTrackQuestion = updateSalesPlaybookQuestion;
export const deleteTalkTrackQuestion = deleteSalesPlaybookQuestion;

// ============================================
// Call Insights Functions
// ============================================

export const updateCallInsightsReview = async (
  sessionId: string,
  reviewData: { overall_comment: string; section_comments: Record<string, string> }
): Promise<ApiResponse> => {
  try {
    return await apiService.patch(`/admin/sessions/${sessionId}/call-insights-review`, reviewData);
  } catch (error) {
    console.error('[CallInsightsAPI] Error saving manager review:', error);
    throw error;
  }
};

export const triggerCallInsightsReanalysis = async (sessionId: string): Promise<ApiResponse> => {
  try {
    return await apiService.post(`/admin/sessions/${sessionId}/call-insights-reanalyze`);
  } catch (error) {
    console.error('[CallInsightsAPI] Error triggering reanalysis:', error);
    throw error;
  }
};

export default apiService;